import { Injectable } from '@angular/core';
import { CustomPageType } from '@core/const';
import { CustomBasePageMetaResolver, PageSubTitleResolver } from '@core/feature';
import {
  BreadcrumbMeta,
  CanonicalPageResolver,
  PageBreadcrumbResolver,
  PageHeadingResolver,
  PageMetaResolver,
  PageRobotsMeta,
  PageRobotsResolver,
  PageTitleResolver,
  ProductSearchPage,
} from '@spartacus/core';
import { Observable, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { BaseProductListPageMetaResolver } from './base-product-list-page-meta.resolver';

@Injectable({
  providedIn: 'root',
})
export class PromoCampaignPageMetaResolver
  extends PageMetaResolver
  implements
    PageTitleResolver,
    PageHeadingResolver,
    PageSubTitleResolver,
    PageBreadcrumbResolver,
    PageRobotsResolver,
    CanonicalPageResolver
{
  constructor(
    private basePageMetaResolver: CustomBasePageMetaResolver,
    private baseProductListPageMetaResolver: BaseProductListPageMetaResolver,
  ) {
    super();
    this.pageType = CustomPageType.CampaignPage as any;
    this.pageTemplate = 'SdPromoCampaignPageTemplate';
  }

  resolveTitle(): Observable<string> {
    return combineLatest({
      pageTitle: this.basePageMetaResolver.resolveTitle(),
      currentBreadcrumbTitle: this.getCurrentBreadcrumbTitle(),
      brPromoCategory: this.resolveBreadcrumbs().pipe(
        map((breadcrumbs?: BreadcrumbMeta[]) => breadcrumbs?.slice(1) || []),
      ),
    }).pipe(
      map(({ pageTitle, currentBreadcrumbTitle, brPromoCategory }) => {
        return brPromoCategory.length > 1 ? currentBreadcrumbTitle : pageTitle;
      }),
    );
  }

  resolveHeading(): Observable<string | undefined> {
    return this.resolveTitle();
  }

  resolveSubTitle(): Observable<string> {
    return this.baseProductListPageMetaResolver.getSubTitle();
  }

  resolveBreadcrumbs(): Observable<BreadcrumbMeta[]> {
    return combineLatest({
      brPage: this.basePageMetaResolver.resolveBreadcrumbs(),
      brPromo: this.baseProductListPageMetaResolver
        .getSearchResults()
        .pipe(switchMap((p: ProductSearchPage) => (p.breadcrumbs ? this.getBreadcrumbData(p) : of(null)))),
    }).pipe(
      map(({ brPage, brPromo }) => {
        if (!brPromo) {
          return brPage;
        }

        if (brPage[1] && !brPage[1].link && brPromo[1] && brPromo[1].link) {
          brPage[1].link = brPromo[1].link;
        }

        return [...brPage, ...brPromo.slice(2)];
      }),
      map((crumbs) => {
        return [...crumbs.slice(0, 1), { label: 'Акции и спецпредложения', link: '/promo' }, ...crumbs.slice(1)];
      }),
    );
  }

  resolveRobots(): Observable<PageRobotsMeta[]> {
    return this.basePageMetaResolver.resolveRobots();
  }

  resolveCanonicalUrl(): Observable<string> {
    return this.basePageMetaResolver.resolveCanonicalUrl();
  }

  private getCurrentBreadcrumbTitle(): Observable<string> {
    return this.resolveBreadcrumbs().pipe(
      map((crumbs: BreadcrumbMeta[]) => {
        return crumbs?.slice(-1)[0]?.label;
      }),
    );
  }

  private getBreadcrumbData(page: ProductSearchPage): Observable<BreadcrumbMeta[]> {
    const breadcrumbs: BreadcrumbMeta[] = [];
    breadcrumbs.push({ label: 'Главная', link: '/' });

    if (page.breadcrumbs) {
      const compaignBreadcrumb = page.breadcrumbs.find((br) => br.facetCode === 'allCampaigns');

      if (compaignBreadcrumb) {
        breadcrumbs.push({
          label: compaignBreadcrumb.facetValueName,
          link: `/p/${compaignBreadcrumb.facetValueCode}/`,
        });
      }

      const parentPage = breadcrumbs[breadcrumbs.length - 1];

      for (const br of page.breadcrumbs) {
        if (br.facetCode === 'category' || br.facetCode === 'allCategories') {
          const link = `${parentPage.link}${br.facetValueCode}/`;

          breadcrumbs.push({
            link,
            label: br.facetValueName,
          });
        }
      }
    }

    return of(breadcrumbs);
  }
}
